/**
 * Helpers for the recorded day map: turning a ski date into its storage folder
 * name, and walking a day's time-sorted track during playback.
 */

/** A pause longer than this breaks the drawn track, so a gap isn't a straight line. */
export const TRACK_GAP_SECONDS = 120;

/** 'YYYY-MM-DD' → the MM_DD_YYYY name of a statsDebugging day folder. */
export const skiDateToRecordedDay = (skiDate) => {
  const [year, month, day] = skiDate.split('-');
  return `${month}_${day}_${year}`;
};

/** MM_DD_YYYY → a readable date. Anything else comes back unchanged. */
export const formatRecordedDay = (recordedDay) => {
  const [month, day, year] = recordedDay.split('_').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? recordedDay : date.toLocaleDateString([], { dateStyle: 'medium' });
};

/** Index of the last location at or before `time`, or 0 when `time` is before the track. */
export const findLocationIndex = (locations, time) => {
  let low = 0;
  let high = locations.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (locations[middle].timestamp <= time) low = middle;
    else high = middle - 1;
  }
  return low;
};

/**
 * The time one playback tick moves to: `step` seconds ahead, or the next
 * recorded point when that lands inside a gap, so playback never idles on one.
 */
export const nextPlaybackTime = (locations, time, step) => {
  const endTime = locations[locations.length - 1].timestamp;
  const target = time + step;
  if (target >= endTime) return endTime;

  const index = findLocationIndex(locations, target);
  return locations[index].timestamp > time ? target : locations[index + 1].timestamp;
};

/**
 * Split a track at recording gaps into runs of [lon, lat] coordinates. Each run
 * keeps `startIndex`, its first point's position in `locations`.
 */
export const splitTrackAtGaps = (locations) => {
  const runs = [];
  locations.forEach((location, index) => {
    const previous = locations[index - 1];
    if (!previous || location.timestamp - previous.timestamp > TRACK_GAP_SECONDS) {
      runs.push({ startIndex: index, coordinates: [] });
    }
    runs[runs.length - 1].coordinates.push([location.lon, location.lat]);
  });
  return runs;
};

/** The runs as drawn up to and including `endIndex`, for the played part of the track. */
export const runsPlayedTo = (runs, endIndex) => runs
  .filter((run) => run.startIndex <= endIndex)
  .map((run) => run.coordinates.slice(0, endIndex - run.startIndex + 1));

/** Runs as a MultiLineString. A one-point run isn't a line, so it's left out. */
export const toMultiLineString = (coordinateRuns) => ({
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'MultiLineString',
    coordinates: coordinateRuns.filter((coordinates) => coordinates.length > 1)
  }
});
